import React from "react";
import styled from "styled-components";
import { convertToRaw, EditorState } from "draft-js";
import { CustomOptionModel, MediaModel } from "../models";
import { CheckOutRedirectTypes, MediaTypes } from "../enums";
import {
  CustomLabel,
  DraftEditor,
  FlexContainer,
  Input,
  IOption,
  ModalComponent,
  SapphireBlueButton,
  SingleSelect,
} from "@mindmemobile/mm-shared-global";
import RedirectInputs from "./RedirectInputs";
import MediaBoxes from "./MediaBoxes";
import FullLibraryView from "./FullLibraryView";
import { toJS } from "mobx";
import FullLibraryViewInPopUp from "./FullLibraryViewInPopUp";
import { observer } from "mobx-react";

type Iprops = {
  customOptionData: CustomOptionModel;
  onSave: (customOptionData: CustomOptionModel) => void;
  togglePopUp: () => void;
};

const CustomOptionsPopupWrapper = styled.div`
  .custom-options-name {
    width: 400px;
  }
  .custom-option-price {
    width: 220px;
  }
  .custom-options-select {
    width: 250px;
    margin-top: 10px;
    .header {
      width: 250px;
    }
  }
  .custom-option-web-url {
    width: 490px;
    min-height: 100px;
    border: 1px solid #aaa;
    border-radius: 4px;
    padding: 6px 7px;
    display: block;
    margin-top: 10px;
    &:focus,
    &:hover {
      outline: none;
    }
  }
  .custom-option-btn {
    width: 250px;
    height: 40px;
    margin-top: 10px;
  }
  .custom-options-media-container {
    margin-top: 5px;
    .label-description-row {
      margin-bottom: 0px;
    }
  }
`;

const CustomOptionPopUp: React.FC<Iprops> = ({
  customOptionData,
  togglePopUp,
  onSave,
}) => {
  const [showLibrary, setShowLibrary] = React.useState(false);
  const [showUpload, setShowUpload] = React.useState(false);
  const [data, setData] = React.useState<CustomOptionModel>(
    new CustomOptionModel(customOptionData)
  );

  React.useEffect(() => {
    const handleEsc = (event: KeyboardEvent) => {
      if (event.key === "Escape" && showLibrary) {
        toggleLibrary(false);
      }
    };

    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [showLibrary]);

  const toggleLibrary = (show: boolean, fromUpload: boolean = false) => {
    setShowLibrary(show);
    if (show) {
      setShowUpload(fromUpload);
    } else {
      setShowUpload(false);
    }
  };

  const handleChange = (
    field: keyof CustomOptionModel,
    value: string | EditorState | boolean | MediaModel
  ) => {
    const clonedData = new CustomOptionModel({ ...data });
    switch (field) {
      case "name":
        clonedData.name = value as string;
        break;
      case "descriptionEditorState":
        const descriptionEditorState = value as EditorState;
        clonedData.descriptionEditorState = descriptionEditorState;
        clonedData.description = descriptionEditorState
          .getCurrentContent()
          .getPlainText();
        clonedData.descriptionEditorRawContent = JSON.stringify(
          convertToRaw(descriptionEditorState.getCurrentContent())
        );
        break;
      case "media":
        clonedData.media = value as MediaModel;
        break;
      case "priceText":
        clonedData.priceText = value as string;
        break;
      case "redirectType":
        clonedData.redirectType = value as keyof typeof CheckOutRedirectTypes;
        break;
      case "showPrice":
        clonedData.showPrice = value as boolean;
        break;
      case "redirectValue":
        clonedData.redirectValue = value as string;
        break;
      case "showButton":
        clonedData.showButton = value as boolean;
        break;
      case "buttonText":
        clonedData.buttonText = value as string;
        break;
    }
    setData(clonedData);
  };

  const deleteMedia = (
    field: keyof CustomOptionModel,
    media: MediaModel | undefined
  ) => {
    const clonedData = new CustomOptionModel({ ...data });
    clonedData.media = media;
    setData(clonedData);
  };

  const textInput = (
    field: keyof CustomOptionModel,
    type: string,
    value: string,
    placeHolder: string,
    className: string,
    error: boolean,
    errorMessage: string
  ) => {
    return (
      <Input
        type={type as "text" | "textarea"}
        value={value}
        error={error}
        errorMessage={errorMessage}
        className={`styled-input merchant-inputs ${className}`}
        onChange={(e) => handleChange(field, e.target.value)}
        placeholder={placeHolder}
      />
    );
  };

  const checkBox = (
    fieldName: keyof CustomOptionModel,
    value: boolean,
    label: string
  ) => {
    return (
      <Input
        value={value}
        id={`chk${fieldName}`}
        type="checkbox"
        label={label}
        onChange={(e) => handleChange(fieldName, e.target.checked)}
      />
    );
  };

  const singleSelect = (
    selectedKey: string,
    fieldName: keyof CustomOptionModel,
    options: IOption[],
    hasError: boolean,
    className?: string
  ) => {
    return (
      <SingleSelect
        hasError={hasError}
        selectedKey={selectedKey}
        nameKeys={["label"]}
        valueKeys={["value"]}
        options={[options]}
        onChange={(option) => {
          handleChange(fieldName, option.value);
        }}
        className={className}
      />
    );
  };

  const redirectOptions = () =>
    Object.keys(CheckOutRedirectTypes).map((key) => {
      return {
        label: CheckOutRedirectTypes[key as keyof typeof CheckOutRedirectTypes],
        value: key,
      };
    });

  const renderRedirectInputs = () => {
    return (
      <>
        {singleSelect(
          data.redirectType,
          "redirectType",
          redirectOptions(),
          false,
          "custom-options-select"
        )}
        <RedirectInputs
          type={data.redirectType}
          value={data.redirectValue}
          onChange={(value: string | boolean | EditorState) =>
            handleChange("redirectValue", value)
          }
          hasError={data.hasError("redirectValue")}
          className="custom-option-web-url"
        />
      </>
    );
  };

  const body = () => {
    return (
      <FlexContainer fxLayout="column" alignItems="center" spacing={30}>
        <FlexContainer fxLayout="column" spacing={15}>
          <CustomLabel>Name</CustomLabel>
          {textInput(
            "name",
            "text",
            data.name,
            "Name",
            "custom-options-name",
            data.hasError("name"),
            "please enter name"
          )}
        </FlexContainer>
        <FlexContainer fxLayout="column" spacing={15}>
          <CustomLabel>Description</CustomLabel>
          <DraftEditor
            editorState={data.descriptionEditorState}
            onChange={(newValue: EditorState) =>
              handleChange("descriptionEditorState", newValue)
            }
            className="product-detail-editor"
          />
        </FlexContainer>
        <FlexContainer
          fxLayout="column"
          spacing={15}
          className="custom-options-media-container"
        >
          <CustomLabel>Media</CustomLabel>
          <MediaBoxes
            key={"medias"}
            hideLabel={true}
            multiple={false}
            medias={(data.media && [data.media]) || []}
            onSelectLibraryMedia={(media) =>
              handleChange("media", MediaModel.mediaItemToMediaModel(media))
            }
            onUpload={(medias: MediaModel[]) =>
              handleChange("media", medias[0])
            }
            onDelete={() => {
              deleteMedia("media", undefined);
            }}
            allowedMediaTypes={[MediaTypes.Image, MediaTypes.Video]}
            toggleLibrary={(show, fromUpload) =>
              toggleLibrary(show, fromUpload)
            }
            showLibrary={showLibrary}
            className="lib-overlay-payment"
          />
        </FlexContainer>
        <FlexContainer fxLayout="column" spacing={15}>
          <CustomLabel>Price</CustomLabel>
          {checkBox("showPrice", data.showPrice, "Show Price")}
          {data.showPrice &&
            textInput(
              "priceText",
              "text",
              data.priceText,
              "Price",
              "custom-option-price",
              data.hasError("priceText"),
              "Please enter price text"
            )}
        </FlexContainer>
        <FlexContainer fxLayout="column" spacing={15}>
          <CustomLabel>Button</CustomLabel>
          {checkBox("showButton", data.showButton, "Show Button")}
          {data.showButton && (
            <>
              {renderRedirectInputs()}
              <Input
                value={data.buttonText}
                id={`txtButtonText`}
                type="text"
                placeholder="Button Text"
                error={data.hasError("buttonText")}
                onChange={(e) => handleChange("buttonText", e.target.value)}
                className="styled-input custom-option-btn"
              />
            </>
          )}
        </FlexContainer>
      </FlexContainer>
    );
  };

  const onClickSave = () => {
    const isValid = data.validate();
    if (!isValid) {
      setData((data) => new CustomOptionModel({ ...data }));
      return;
    }
    onSave(data);
  };

  if (showLibrary) {
    return (
      <FullLibraryViewInPopUp
        multiple={false}
        hideLabel={true}
        medias={(data.media && [data.media]) || []}
        onUpload={(medias: MediaModel[]) => handleChange("media", medias[0])}
        onDelete={() => deleteMedia("media", undefined)}
        onSelectLibraryMedia={(media) =>
          handleChange("media", MediaModel.mediaItemToMediaModel(media))
        }
        allowedMediaTypes={[MediaTypes.Image, MediaTypes.Video]}
        toggleLibrary={(show: boolean, fromUpload?: boolean) => {
          toggleLibrary(show, fromUpload);
        }}
        showLibrary={showLibrary}
        updateMedia={() => {}}
        showUpload={showUpload}
      />
    );
  }
  return (
    <CustomOptionsPopupWrapper>
      <ModalComponent
        title="Custom Option"
        titleFontSize="22px"
        bodyPadding="40px 50px"
        showFooter={true}
        minHeight="440px"
        wrapperWidth="900px"
        bodyBackgroundColor="#fefefe"
        footerBackground="#F4F7FB"
        footerHeight="auto"
        onClose={() => togglePopUp()}
        footerContent={
          <FlexContainer
            alignItems="center"
            justifyContent="center"
            spacing={15}
          >
            <SapphireBlueButton
              minWidth="120px"
              height="40px"
              onClick={() => togglePopUp()}
            >
              Close
            </SapphireBlueButton>
            <SapphireBlueButton
              primary
              minWidth="120px"
              height="40px"
              onClick={() => onClickSave()}
            >
              Save
            </SapphireBlueButton>
          </FlexContainer>
        }
      >
        {body()}
      </ModalComponent>
    </CustomOptionsPopupWrapper>
  );
};

export default observer(CustomOptionPopUp);
